import { notNull } from "@msys/common";
import { Modal, StatusChip, TabOption, Tabs, useScreenWidth } from "@msys/ui";
import { useLocalStorageAsState } from "@msys/ui";
import { Category as CategoryIcon } from "@mui/icons-material";
import { ListAltOutlined as ListAltOutlinedIcon } from "@mui/icons-material";
import { ShoppingCartOutlined as ShoppingCartOutlinedIcon } from "@mui/icons-material";
import { Box, Stack, SvgIconProps, useTheme } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Formik, FormikProps, useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import {
  ItemType,
  TemplateSearchSortingInput,
  TplApplicableFor,
} from "../../../clients/graphqlTypes.js";
import { FullScreenToggleIconButton } from "../../commons/button/FullScreenToggleIconButton.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { track } from "../../track.js";
import { ProductFilterFields_AvailableFiltersQueryVariables } from "../products/filters/ProductFilterFields.generated.js";
import { ProductListSelectMultipleComponent } from "../productLists/ProductListSelectMultipleModal.js";
import { ProductSelectMultipleComponent } from "../products/modals/ProductSelectMultipleModal.js";
import { TemplateFiltersFormValues } from "../templates/filters/TemplateFilterFields.js";
import { TemplatesQuoteSelectMultipleComponent } from "../templates/quote/TemplatesQuoteSelectMultipleModal.js";
import { useTemplatesPickerQueryState } from "../templates/quote/useTemplatesQueryVariables.js";
import { TemplateIcon } from "../templates/TemplateIcon.js";
import {
  SearchItemInputDestination,
  SearchItemInputDestinationContext,
} from "./SearchItemInputDestinationContext.js";
import {
  CartTab,
  FormValues,
  SearchProductsAndTemplatesForm,
  SearchTab,
} from "./SearchProductsAndTemplates.js";

export type { SearchItemInputDestination } from "./SearchItemInputDestinationContext.js";
export { useSearchItemInputDestination } from "./SearchItemInputDestinationContext.js";

export const SearchItemInputModal = ({
  handleClose,
  handleSelect,
  ...props
}: {
  handleSelect(values: FormValues): Promise<void> | void;
  handleClose(): void;
  initialSearchValue: string;
  initialSearchTab: SearchTab;
  excludeTemplateIds?: string[];
  rootItemTypes?: ItemType[];
  applicableFor?: TplApplicableFor[];
  allowedSearchTabs?: SearchTab[];
  AddToListIcon?: React.ComponentType<SvgIconProps>;
  showQuantityInput?: boolean;
  /** Modal heading. With a title, tabs render in the body below it;
   *  without one, the tabs themselves become the title row. */
  title?: string;
  /** Names what's being inserted into — a tag beside `title`, and the
   *  confirm button's "Insert N items into {title}" copy. */
  destination?: SearchItemInputDestination;
  /** Seeds the templates tab's property filters. */
  initialTemplateFilters?: TemplateFiltersFormValues;
  /** Seeds the templates tab's property sortings. Appended after the
   *  user's own column choice, which stays primary. */
  seedPropertySortings?: TemplateSearchSortingInput[];
  initialProductListId?: string;
  initialProductListTitle?: string;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["ProductSearch", "TemplatesSearch", "Global"]);

  const initialValues = React.useMemo(
    (): FormValues => ({
      products: [],
      templates: [],
    }),
    []
  );

  const [variables, setVariables] =
    React.useState<ProductFilterFields_AvailableFiltersQueryVariables>({});

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const formikRef = React.useRef<FormikProps<FormValues>>(null);

  const handleCloseWithCheck = async () => {
    if (
      (formikRef.current?.values.products.length ?? 0) +
        (formikRef.current?.values.templates.length ?? 0) >
      0
    ) {
      const result = await startConfirmProcess({
        title: t("Add selected items?", {
          ns: "ProductSearch",
        }),
        text: t(
          "You have selected items but have not added them to your document. Would you like to add them?",
          {
            ns: "ProductSearch",
          }
        ),
        confirmButtonLabel: t("Yes, add items", {
          ns: "ProductSearch",
        }),
        cancelButtonLabel: t("Discard items", {
          ns: "ProductSearch",
        }),
      });
      if (result) {
        await formikRef.current?.submitForm();
      }
      if (result !== undefined) {
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  return (
    <>
      <Formik<FormValues>
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={async values => {
          try {
            const products = values.products.filter(p => p.quantity > 0);
            const templates = values.templates.filter(p => p.quantity > 0);
            if (products.length === 0 && templates.length === 0) return;

            track({
              eventName: "AddSelectedProducts",
              data: { products, variables },
            });
            await handleSelect({ products, templates });
            handleClose();
          } catch (e) {
            if (e instanceof Error) {
              if (e.message.includes("Cycle in templates detected"))
                enqueueSnackbar(
                  t("Creating cycle linking in templates is not allowed", {
                    ns: "TemplatesSearch",
                  }),
                  { variant: "error" }
                );
              else enqueueSnackbar(e.message, { variant: "error" });
            }
            throw e;
          }
        }}
      >
        <SearchItemInputModalComponent
          handleClose={handleCloseWithCheck}
          onProductSearchVariablesChange={setVariables}
          {...props}
        />
      </Formik>
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  );
};

interface Props {
  initialSearchValue: string;
  initialSearchTab: SearchTab;
  excludeTemplateIds?: string[];
  rootItemTypes?: ItemType[];
  applicableFor?: TplApplicableFor[];
  handleClose(): void;
  allowedSearchTabs?: SearchTab[];
  onProductSearchVariablesChange?(
    variables: ProductFilterFields_AvailableFiltersQueryVariables
  ): void;
  AddToListIcon?: React.ComponentType<SvgIconProps>;
  showQuantityInput?: boolean;
  title?: string;
  destination?: SearchItemInputDestination;
  initialTemplateFilters?: TemplateFiltersFormValues;
  seedPropertySortings?: TemplateSearchSortingInput[];
  initialProductListId?: string;
  initialProductListTitle?: string;
}

export const SearchItemInputModalComponent = ({
  initialSearchValue,
  initialSearchTab,
  excludeTemplateIds,
  rootItemTypes,
  applicableFor,
  handleClose,
  allowedSearchTabs,
  onProductSearchVariablesChange,
  AddToListIcon,
  showQuantityInput,
  title,
  destination,
  initialTemplateFilters,
  seedPropertySortings,
  initialProductListId,
  initialProductListTitle,
}: Props) => {
  const { t } = useTranslate([
    "TemplatesSearch",
    "ProductSearch",
    "ProductLists",
  ]);
  const { isMaxPhone } = useScreenWidth();

  const formikProps = useFormikContext<FormValues>();

  const [searchTab, setSearchTab] = React.useState<SearchTab | null>(
    initialSearchTab
  );
  const [searchValue, setSearchValue] =
    React.useState<string>(initialSearchValue);

  const latestSearchValue = React.useRef<string>(initialSearchValue);

  const handleSearchValueChange = React.useCallback((value: string) => {
    latestSearchValue.current = value;
  }, []);

  // Owned here, not inside the templates results component, so switching to
  // the Products tab and back doesn't discard the user's filter edits — the
  // templates component unmounts on that switch, and the placeholder-fill
  // flow's own "Build it from products" action performs exactly that switch.
  const templatesQueryState = useTemplatesPickerQueryState({
    hasDestination: !!destination,
    initialTemplateFilters,
    hasSeedPropertySortings: (seedPropertySortings?.length ?? 0) > 0,
    initialSearchTerm: initialSearchValue,
  });

  const handleSearchTabChange = React.useCallback(
    (newTab: SearchTab | null) => {
      // first setting new search value, then setting new tab
      setSearchValue(latestSearchValue.current);
      setSearchTab(newTab);
    },
    []
  );

  const searchTabsOptions = React.useMemo(
    () =>
      [
        !allowedSearchTabs || allowedSearchTabs.includes("products")
          ? {
              value: "products" as SearchTab,
              // used in case if it's a single tab
              title: t("Select Products", {
                ns: "ProductSearch",
              }),
              label: t("Products", {
                ns: "ProductSearch",
              }),
              icon: <CategoryIcon />,
            }
          : null,
        !allowedSearchTabs || allowedSearchTabs.includes("templates")
          ? {
              value: "templates" as SearchTab,
              label: t("Templates", {
                ns: "TemplatesSearch",
              }),
              // used in case if it's a single tab
              title: t("Select Templates", {
                ns: "TemplatesSearch",
              }),
              icon: <TemplateIcon />,
            }
          : null,
        !allowedSearchTabs || allowedSearchTabs.includes("productLists")
          ? {
              value: "productLists" as SearchTab,
              label: t("Product lists", {
                ns: "ProductLists",
              }),
              // used in case if it's a single tab
              title: t("Product lists", {
                ns: "ProductLists",
              }),
              icon: <ListAltOutlinedIcon />,
            }
          : null,
      ].filter(notNull),
    [t, allowedSearchTabs]
  );

  const cartTabsOptions = React.useMemo(
    () => [
      {
        value: "cart" as CartTab,
        label: "",
        count:
          formikProps.values.products.length +
          formikProps.values.templates.length,
        icon: <ShoppingCartOutlinedIcon />,
      },
    ],
    [formikProps.values.products.length, formikProps.values.templates.length]
  );

  const Form = (
    <SearchProductsAndTemplatesForm
      searchTab={searchTab}
      showQuantityInput={showQuantityInput}
    />
  );

  // Titled mode, desktop only: handed to the results component so it can
  // render level with the cart's own header, instead of its own full-width
  // row above (see the `title ?` body branch below).
  const desktopTabsSlot =
    title && !isMaxPhone && searchTabsOptions.length > 1 ? (
      <Tabs<SearchTab, TabOption<SearchTab>>
        onChange={handleSearchTabChange}
        value={searchTab ?? undefined}
        options={searchTabsOptions}
      />
    ) : undefined;

  React.useEffect(() => {
    if (!searchTab && !isMaxPhone) {
      handleSearchTabChange(allowedSearchTabs?.[0] || "products");
    }
  }, [searchTab, isMaxPhone, allowedSearchTabs, handleSearchTabChange]);

  const theme = useTheme();

  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    "msys-search-item-input-modal-fullscreen",
    false,
    true
  );

  return (
    <SearchItemInputDestinationContext.Provider value={destination ?? null}>
      <Modal
        dialogProps={{
          maxWidth: "xl",
          slotProps: { paper: { style: { height: "100%" } } },
          ...(!isMaxPhone && isFullScreen ? { fullScreen: true } : undefined),
        }}
        titleProps={{
          sx: {
            position: "relative",
            zIndex: 2,
            backgroundColor: theme.palette.common.white,
          },
        }}
        headerActions={
          !isMaxPhone ? (
            <FullScreenToggleIconButton
              isFullScreen={isFullScreen}
              setIsFullScreen={setIsFullScreen}
            />
          ) : undefined
        }
        handleClose={handleClose}
        title={
          title ? (
            // destination renders as a StatusChip tag (same as the quote
            // title's "Draft" badge), tighter corner radius.
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <span>{title}</span>
              {destination && (
                <Box sx={{ "& .MuiChip-root": { borderRadius: "4px" } }}>
                  <StatusChip
                    label={destination.title}
                    color="default"
                    size="small"
                  />
                </Box>
              )}
            </Stack>
          ) : searchTabsOptions.length === 1 && !isMaxPhone ? (
            <>
              <Box
                sx={{
                  mr: 1,
                  display: "flex",
                  flexGrow: 0,
                  flexShrink: 0,
                }}
              >
                {searchTabsOptions[0].icon}
              </Box>
              <span>{searchTabsOptions[0].title}</span>
            </>
          ) : (
            // No title: the tabs themselves are the title row.
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                flex: 1,
              }}
            >
              <Tabs<SearchTab, TabOption<SearchTab>>
                onChange={handleSearchTabChange}
                value={searchTab ?? undefined}
                options={searchTabsOptions}
              />
              {isMaxPhone && (
                <Tabs<CartTab, TabOption<CartTab>>
                  onChange={() => handleSearchTabChange(null)}
                  value={!searchTab ? "cart" : undefined}
                  options={cartTabsOptions}
                />
              )}
            </Stack>
          )
        }
        // Without this, the modal-stack registration effect makes it render
        // display:none on its first frame, which silently defeats MUI's
        // one-shot auto-focus — Escape then does nothing until the user
        // clicks something inside to focus it manually.
        notInStack
      >
        {title ? (
          // Titled: tabs move to the body, in a fixed-height column so
          // only the results area scrolls. On mobile there's no side-by-side
          // results/cart layout to align with, so the tabs stay in their own
          // row above; on desktop they're handed to the results component to
          // render level with the cart's own header instead (see `tabs` prop).
          <Stack direction="column" sx={{ height: "100%", overflow: "hidden" }}>
            {searchTabsOptions.length > 1 && isMaxPhone && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  pb: 1,
                }}
              >
                <Tabs<SearchTab, TabOption<SearchTab>>
                  onChange={handleSearchTabChange}
                  value={searchTab ?? undefined}
                  options={searchTabsOptions}
                />
                <Tabs<CartTab, TabOption<CartTab>>
                  onChange={() => handleSearchTabChange(null)}
                  value={!searchTab ? "cart" : undefined}
                  options={cartTabsOptions}
                />
              </Stack>
            )}
            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              {searchTab === "products" && (
                <ProductSelectMultipleComponent
                  initialSearchValue={searchValue}
                  handleSearchValueChange={handleSearchValueChange}
                  Form={Form}
                  onProductSearchVariablesChange={
                    onProductSearchVariablesChange
                  }
                  AddToListIcon={AddToListIcon}
                  tabs={desktopTabsSlot}
                />
              )}
              {searchTab === "templates" && (
                <TemplatesQuoteSelectMultipleComponent
                  excludeTemplateIds={excludeTemplateIds}
                  rootItemTypes={rootItemTypes}
                  applicableFor={applicableFor}
                  initialSearchValue={searchValue}
                  handleSearchValueChange={handleSearchValueChange}
                  Form={Form}
                  AddToListIcon={AddToListIcon}
                  showQuantityInput={showQuantityInput}
                  initialTemplateFilters={initialTemplateFilters}
                  seedPropertySortings={seedPropertySortings}
                  queryState={templatesQueryState}
                  onSwitchToProductsTab={() =>
                    handleSearchTabChange("products")
                  }
                  tabs={desktopTabsSlot}
                />
              )}
              {searchTab === "productLists" && (
                <ProductListSelectMultipleComponent
                  initialSearchValue={searchValue}
                  handleSearchValueChange={handleSearchValueChange}
                  Form={Form}
                  AddToListIcon={AddToListIcon}
                  initialProductListId={initialProductListId}
                  initialProductListTitle={initialProductListTitle}
                  tabs={desktopTabsSlot}
                />
              )}
              {searchTab === null && isMaxPhone && (
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {Form}
                </Stack>
              )}
            </Box>
          </Stack>
        ) : (
          <>
            {searchTab === "products" && (
              <ProductSelectMultipleComponent
                initialSearchValue={searchValue}
                handleSearchValueChange={handleSearchValueChange}
                Form={Form}
                onProductSearchVariablesChange={onProductSearchVariablesChange}
                AddToListIcon={AddToListIcon}
              />
            )}
            {searchTab === "templates" && (
              <TemplatesQuoteSelectMultipleComponent
                excludeTemplateIds={excludeTemplateIds}
                rootItemTypes={rootItemTypes}
                applicableFor={applicableFor}
                initialSearchValue={searchValue}
                handleSearchValueChange={handleSearchValueChange}
                Form={Form}
                AddToListIcon={AddToListIcon}
                showQuantityInput={showQuantityInput}
                initialTemplateFilters={initialTemplateFilters}
                seedPropertySortings={seedPropertySortings}
                queryState={templatesQueryState}
                onSwitchToProductsTab={() => handleSearchTabChange("products")}
              />
            )}
            {searchTab === "productLists" && (
              <ProductListSelectMultipleComponent
                initialSearchValue={searchValue}
                handleSearchValueChange={handleSearchValueChange}
                Form={Form}
                AddToListIcon={AddToListIcon}
                initialProductListId={initialProductListId}
                initialProductListTitle={initialProductListTitle}
              />
            )}
            {searchTab === null && isMaxPhone && (
              <Stack
                direction="column"
                spacing={1}
                sx={{
                  flex: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                {Form}
              </Stack>
            )}
          </>
        )}
      </Modal>
    </SearchItemInputDestinationContext.Provider>
  );
};
